import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { getToken } from "utils/auth";
import { fireSwal } from "utils/swalConfig";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const useClassroomManager = (classroomId) => {
    const [classroom, setClassroom] = useState(null);
    const [students, setStudents] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const studentTeamMap = useMemo(() => {
        const newMap = new Map();
        teams.forEach(team => {
            team.members?.forEach(member => newMap.set(member.id, team.name));
        });
        return newMap;
    }, [teams]);

    const unassignedStudents = useMemo(() => {
        return students.filter(s => !studentTeamMap.has(s.id));
    }, [students, studentTeamMap]);

    const fetchTeams = useCallback(async () => {
        if (!classroomId) return;
        try {
            const token = getToken();
            const res = await axios.get(`${API_BASE}/api/teams/classrooms/${classroomId}/teams`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTeams(res.data || []);
        } catch (err) {
            console.error("Failed to load teams", err);
            setError("Failed to load team data.");
        }
    }, [classroomId]);

    const fetchClassroomDetails = useCallback(async () => {
        if (!classroomId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const token = getToken();
            const [classroomRes, teamsRes] = await Promise.all([
                axios.get(`${API_BASE}/api/classrooms/${classroomId}`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_BASE}/api/teams/classrooms/${classroomId}/teams`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setClassroom(classroomRes.data);
            setStudents(classroomRes.data.pupils || []);
            setTeams(teamsRes.data || []);
        } catch (err) {
            console.error("Failed to load classroom details", err);
            setError("Failed to load classroom details.");
        } finally {
            setLoading(false);
        }
    }, [classroomId]);

    useEffect(() => {
        fetchClassroomDetails();
    }, [fetchClassroomDetails]);

    const handleDeleteStudent = async (studentId) => {
        const result = await fireSwal({
            title: 'Are you sure?',
            text: "This student will be removed from the classroom and any team they are in.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove them!',
            cancelButtonText: 'No, cancel!',
            actionType: 'delete',
        });
        if (!result.isConfirmed) return;

        try {
            const token = getToken();
            await axios.delete(`${API_BASE}/api/classrooms/students/${studentId}`, { headers: { Authorization: `Bearer ${token}` } });
            setStudents(prev => prev.filter(s => s.id !== studentId));
            fireSwal({ title: 'Removed!', text: 'The student has been removed.', icon: 'success' });
        } catch (err) {
            console.error("Failed to delete student", err);
            fireSwal({ title: 'Error!', text: 'Failed to remove the student.', icon: 'error', actionType: 'delete' });
        }
    };

    const handleAddStudent = async (fullName, username, school) => {
        try {
            const token = getToken();
            const res = await axios.post(
                `${API_BASE}/api/classrooms/${classroomId}/students`,
                { fullName, username, school },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setStudents(prev => [...prev, res.data]);
            fireSwal({ title: 'Success!', text: 'New student has been added.', icon: 'success' });
            return { success: true };
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Failed to add student.';
            fireSwal({ title: 'Error!', text: errorMsg, icon: 'error', actionType: 'delete' });
            return { success: false };
        }
    };

    const handleCreateTeam = async (teamCreationData) => {
        try {
            const token = getToken();
            await axios.post(`${API_BASE}/api/teams/classroom-team/create`, teamCreationData, { headers: { Authorization: `Bearer ${token}` } });
            await fetchTeams();
            fireSwal({ icon: 'success', title: 'Team Created!', text: `'${teamCreationData.name}' has been successfully created.` });
            return { success: true };
        } catch (err) {
            const errorMsg = err.response?.data?.message || "An unexpected error occurred.";
            fireSwal({ title: 'Creation Failed', text: `There was an issue: ${errorMsg}`, icon: 'error', actionType: 'delete' });
            return { success: false };
        }
    };

    const handleAssignStudent = async (studentId, teamId, role) => {
        try {
            const token = getToken();
            await axios.post(`${API_BASE}/api/teams/${teamId}/members`, { studentId, role }, { headers: { Authorization: `Bearer ${token}` } });
            await fetchTeams();
            fireSwal({ title: 'Success!', text: 'Student has been assigned to the team.', icon: 'success' });
        } catch (err) {
            fireSwal({ title: 'Assignment Failed!', text: err.response?.data?.message || 'There was an issue assigning the student.', icon: 'error', actionType: 'delete' });
        }
    };

    return {
        loading,
        error,
        classroom,
        students,
        teams,
        studentTeamMap,
        unassignedStudents,
        fetchTeams,
        handleDeleteStudent,
        handleAddStudent,
        handleCreateTeam,
        handleAssignStudent
    };
};